import React, { useState } from 'react';
import {
    ArrowheadLeftLarge,
    ArrowheadRightLarge,
    ExternallinkSmall,
    VideoCameraOutlineSmall,
    VideoCameraFillSmall
} from '@blueprint/icons';
import { CTALink } from 'listing-details/components/CTA/CTA';
import {
    VirtualTourContainer,
    SupportedTour,
    UnsupportedTour,
    NavButton,
    NavControls,
    ProgressIndicator,
    Error
} from './VirtualTours.styles';
import {
    trackNextVirtualTour,
    trackPreviousVirtualTour,
    trackExternalVirtualTour
} from './VirtualTours.tracking';
import { buildCombinedVirtualTours } from 'listing-details/helpers';
import { VirtualToursProps } from './VirtualTours.types';
import { Tag } from '@blueprint/ui';

const VirtualTours = ({ data }: VirtualToursProps) => {
    const [tourIndex, setTourIndex] = useState(0);

    const {
        embeddedContent: { tours, videos, links }
    } = data;

    const virtualTours = buildCombinedVirtualTours(
        tours || [],
        videos || [],
        links || []
    );

    if (!virtualTours?.length) {
        return <Error>There was an error loading the virtual tours</Error>;
    }

    const currentTour = virtualTours[tourIndex];

    const onPreviousTour = () => {
        if (tourIndex > 0) {
            trackPreviousVirtualTour(tourIndex);
            setTourIndex(tourIndex - 1);
        }
    };

    const onNextTour = () => {
        if (tourIndex < virtualTours.length - 1) {
            trackNextVirtualTour(tourIndex);
            setTourIndex(tourIndex + 1);
        }
    };

    return (
        <>
            {virtualTours.length > 1 ? (
                <>
                    <NavControls>
                        <NavButton
                            onClick={onPreviousTour}
                            data-testid="virtual-tour-prev"
                            aria-label="Previous virtual tour"
                            disabled={tourIndex === 0}
                        >
                            <ArrowheadLeftLarge />
                        </NavButton>
                        <NavButton
                            onClick={onNextTour}
                            data-testid="virtual-tour-next"
                            aria-label="Next virtual tour"
                            disabled={tourIndex + 1 === virtualTours.length}
                        >
                            <ArrowheadRightLarge />
                        </NavButton>
                    </NavControls>
                    <ProgressIndicator
                        aria-label={`Displaying virtual tour ${
                            tourIndex + 1
                        } of ${virtualTours.length}`}
                        data-testid="virtual-tour-counter"
                    >
                        <Tag aria-hidden>
                            <VideoCameraFillSmall />
                            {`${tourIndex + 1}/${virtualTours.length}`}
                        </Tag>
                    </ProgressIndicator>
                </>
            ) : null}

            <VirtualTourContainer aria-live="polite">
                {currentTour.isSupported ? (
                    <SupportedTour>
                        <iframe
                            title="Virtual tour"
                            src={currentTour.url}
                            allowFullScreen
                            frameBorder={0}
                            data-testid="virtual-tour-supported-iframe"
                            loading="lazy"
                        />
                    </SupportedTour>
                ) : (
                    <UnsupportedTour>
                        <CTALink
                            href={currentTour.url}
                            target="_blank"
                            label="External virtual tour"
                            icon={<VideoCameraOutlineSmall />}
                            iconAction={
                                <ExternallinkSmall data-testid="icon-link" />
                            }
                            onClick={trackExternalVirtualTour}
                        />
                    </UnsupportedTour>
                )}
            </VirtualTourContainer>
        </>
    );
};

export default VirtualTours;
